import React from "react";

/**
 * Brand icons for the LLM providers we support.
 * Pure SVG (no external CDN) so they render even offline.
 * Each icon accepts a `className` prop. Default size is 1.5rem.
 */
const wrap = (children, viewBox = "0 0 24 24", className = "h-6 w-6") => (
  <svg
    viewBox={viewBox}
    aria-hidden="true"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    {children}
  </svg>
);

export const OpenAIIcon = ({ className }) =>
  wrap(
    <path
      fill="currentColor"
      d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.985 5.985 0 0 0 .516 4.91 6.052 6.052 0 0 0 6.515 2.9 5.985 5.985 0 0 0 4.512 2.018 6.046 6.046 0 0 0 5.766-4.198 5.985 5.985 0 0 0 3.998-2.9 6.046 6.046 0 0 0-.751-7.086zM13.27 21.142a4.482 4.482 0 0 1-2.876-1.04l.143-.082 4.774-2.757a.776.776 0 0 0 .392-.681v-6.737l2.018 1.168a.072.072 0 0 1 .039.057v5.583a4.504 4.504 0 0 1-4.49 4.49zM3.61 17.022a4.482 4.482 0 0 1-.534-3.014l.142.085 4.778 2.758a.778.778 0 0 0 .784 0l5.833-3.367v2.327a.073.073 0 0 1-.029.062L9.76 18.66a4.504 4.504 0 0 1-6.15-1.638zM2.347 7.45a4.479 4.479 0 0 1 2.34-1.97v5.68a.776.776 0 0 0 .39.678l5.806 3.351-2.018 1.166a.075.075 0 0 1-.071 0L4.014 13.59a4.504 4.504 0 0 1-1.667-6.142zm16.582 3.853-5.833-3.387 2.014-1.163a.075.075 0 0 1 .071 0l4.83 2.79a4.49 4.49 0 0 1-.676 8.105v-5.68a.79.79 0 0 0-.406-.665zm2.011-3.012-.142-.087-4.77-2.78a.778.778 0 0 0-.785 0L9.41 8.795V6.468a.072.072 0 0 1 .029-.062l4.83-2.789a4.498 4.498 0 0 1 6.68 4.659zM8.31 12.405l-2.02-1.164a.072.072 0 0 1-.038-.057V5.605a4.504 4.504 0 0 1 7.379-3.453l-.142.083L8.715 4.99a.776.776 0 0 0-.39.681zm1.097-2.365 2.6-1.5 2.6 1.499v2.998l-2.59 1.5-2.6-1.5z"
    />,
  );

export const AnthropicIcon = ({ className }) =>
  wrap(
    <>
      <rect width="24" height="24" rx="4" fill="#C97758" />
      <path
        fill="#fff"
        d="M14.187 6.78h-2.32l4.221 10.44h2.32L14.187 6.78zm-6.55 0L3.412 17.22h2.366l.865-2.243h4.42l.865 2.243h2.366L10.196 6.78H7.637zm.452 6.214 1.469-3.81 1.46 3.81H8.09z"
      />
    </>,
  );

export const GeminiIcon = ({ className }) =>
  wrap(
    <>
      <defs>
        <linearGradient id="gemini-grad" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
          <stop stopColor="#4F8CFF" />
          <stop offset="0.55" stopColor="#9168F0" />
          <stop offset="1" stopColor="#FE6F8B" />
        </linearGradient>
      </defs>
      <path
        fill="url(#gemini-grad)"
        d="M12 2c.39 4.74 4.27 8.61 9 9-4.74.39-8.61 4.27-9 9-.39-4.74-4.27-8.61-9-9 4.74-.39 8.61-4.27 9-9z"
      />
    </>,
  );

export const DeepSeekIcon = ({ className }) =>
  wrap(
    <>
      <rect width="24" height="24" rx="4" fill="#4D6BFF" />
      <path
        fill="#fff"
        d="M7.5 8.5c1.93 0 3.5 1.57 3.5 3.5s-1.57 3.5-3.5 3.5S4 13.93 4 12s1.57-3.5 3.5-3.5zm0 5.5c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zm9-5.5c1.93 0 3.5 1.57 3.5 3.5s-1.57 3.5-3.5 3.5S13 13.93 13 12s1.57-3.5 3.5-3.5zm0 5.5c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z"
      />
    </>,
  );

export const MistralIcon = ({ className }) =>
  wrap(
    <>
      <rect x="2" y="4" width="20" height="3.5" fill="#FFC72C" />
      <rect x="2" y="8.25" width="20" height="3.5" fill="#FF8A1F" />
      <rect x="2" y="12.5" width="20" height="3.5" fill="#FA5012" />
      <rect x="2" y="16.75" width="20" height="3.25" fill="#E10500" />
    </>,
  );

export const GroqIcon = ({ className }) =>
  wrap(
    <>
      <rect width="24" height="24" rx="4" fill="#F55036" />
      <path
        fill="#fff"
        d="M12 5.5c-3.59 0-6.5 2.91-6.5 6.5 0 1.27.36 2.45.99 3.45v2.55h2.55c1 .63 2.18.99 3.45.99 3.59 0 6.5-2.91 6.5-6.5S15.59 5.5 12 5.5zm0 11c-.71 0-1.39-.13-2.01-.36V13.5h3.51v3.36c-.49.09-.99.14-1.5.14zm3-1.07v-3.43H8.99v3.43c-.91-.85-1.49-2.06-1.49-3.43 0-2.49 2.01-4.5 4.5-4.5s4.5 2.01 4.5 4.5c0 1.37-.58 2.58-1.5 3.43z"
      />
    </>,
  );

export const CohereIcon = ({ className }) =>
  wrap(
    <>
      <rect width="24" height="24" rx="4" fill="#39594D" />
      <path
        fill="#D18EE2"
        d="M8.5 14.6c-.7 0-1.4-.2-2-.6-.6-.4-1.1-1-1.4-1.6-.3-.7-.4-1.4-.3-2.1.1-.7.5-1.4 1-1.9.5-.5 1.2-.9 1.9-1.1.7-.1 1.5-.1 2.2.2.7.3 1.3.7 1.7 1.3.4.6.7 1.3.7 2.1H8.5v3.7z"
      />
      <circle cx="14.5" cy="16" r="2" fill="#FF7759" />
      <circle cx="17" cy="12" r="1.5" fill="#FFC2A6" />
    </>,
  );

export const XAIIcon = ({ className }) =>
  wrap(
    <>
      <rect width="24" height="24" rx="4" fill="#000" />
      <path
        fill="#fff"
        d="M5.5 6 11 12l-5.5 6h2.5L13 13l5 5h2L13.5 11 19 6h-2.5L12 11.5 8 6H5.5z"
      />
    </>,
  );

export const PerplexityIcon = ({ className }) =>
  wrap(
    <>
      <rect width="24" height="24" rx="4" fill="#1F8C8C" />
      <path
        fill="#fff"
        d="M12 4.5 5.5 9v9L12 12l6.5 6V9L12 4.5zm0 2.7 4.5 3.1v6.7L12 13l-4.5 4V10.3L12 7.2z"
      />
    </>,
  );

export const OpenRouterIcon = ({ className }) =>
  wrap(
    <>
      <rect width="24" height="24" rx="4" fill="#0F172A" />
      <path
        fill="#7DD3FC"
        d="M4 9.5h4l2 1.5h4l2-1.5h4v2h-4l-2 1.5h-4l-2-1.5H4v-2zm0 4h4l2 1.5h4l2-1.5h4v2h-4l-2 1.5h-4l-2-1.5H4v-2z"
      />
    </>,
  );

export const FallbackIcon = ({ className }) =>
  wrap(
    <>
      <rect width="24" height="24" rx="4" fill="#94A3B8" />
      <path
        fill="#fff"
        d="M12 6a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm0 6.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"
      />
    </>,
  );

const REGISTRY = {
  openai: OpenAIIcon,
  anthropic: AnthropicIcon,
  google: GeminiIcon,
  gemini: GeminiIcon,
  deepseek: DeepSeekIcon,
  mistral: MistralIcon,
  groq: GroqIcon,
  cohere: CohereIcon,
  xai: XAIIcon,
  perplexity: PerplexityIcon,
  openrouter: OpenRouterIcon,
};

export function ProviderIcon({ provider, className }) {
  const C = REGISTRY[provider] || FallbackIcon;
  return <C className={className || "h-6 w-6"} />;
}

export default ProviderIcon;
